import { css } from '@emotion/react';
import {
  forwardRef, useCallback, useEffect, useState,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import {
  guaranteeHash, isValidHex,
} from '../colorPicker.helpers';

const DEFAULT_DIMENSION = 20;

const inputStyle = (theme: Theme) => css({
  height: DEFAULT_DIMENSION,
  textTransform: 'uppercase',
  width: '80%',
  '&[disabled]': {
    opacity: theme.opacity.disabled,
  },
});

type ColorCodeHexFieldProps = Readonly<{
  className?: string;
  disabled?: boolean;
  onChange?: (value: HexColor) => void;
  value: string;
}>;

export const ColorCodeHexFieldComponent = forwardRef<HTMLInputElement, ColorCodeHexFieldProps>((props, inputRef) => {
  const [hexInput, setHexInput] = useState<string>(props.value);

  useEffect(() => {
    setHexInput(props.value);
  }, [props.value]);

  const onHexInputChange = useCallback((value: string) => {
    const hex = guaranteeHash(value);

    // Only full length hex is valid to keep input consistent with ColorResult format
    if (hex.length === 7 && isValidHex(hex) && props.value.toUpperCase() !== hex.toUpperCase()) {
      const onChange = props.onChange;
      onChange?.(hex);
    }
    else {
      setHexInput(value);
    }
  }, [props.onChange, props.value]);

  return (
    <input
      className={props.className}
      css={inputStyle}
      disabled={props.disabled}
      maxLength={7}
      onChange={e => onHexInputChange(e.target.value)}
      ref={inputRef}
      value={guaranteeHash(hexInput)}
    />
  );
});
